import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Target, Flame, Trophy, Clock,
  BarChart3, TrendingUp, Music2, Calendar, Check, Pencil
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart
} from 'recharts';
import useAuthStore from '../store/useAuthStore';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ label, value, icon: Icon, sub, className }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={cn(
      "p-5 bg-card border-2 border-border rounded-2xl brutalist-shadow",
      className
    )}
    data-testid={`progress-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
        {label}
      </span>
      {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
    </div>
    <div className="text-3xl font-bold text-foreground font-mono">{value}</div>
    {sub && <div className="text-sm text-muted-foreground mt-1">{sub}</div>}
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border-2 border-border rounded-xl p-3 shadow-lg text-sm">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} style={{ color: entry.color }}>
          {entry.name}: {entry.value}{entry.name === 'Accuracy' ? '%' : ''}
        </p>
      ))}
    </div>
  );
};

const ProgressRing = ({ pct, size = 140, stroke = 10 }) => {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (Math.min(pct, 100) / 100) * circ;
  const color = pct >= 100 ? 'hsl(160, 84%, 39%)' : 'hsl(24, 95%, 53%)';

  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke="hsl(240, 5%, 96%)" strokeWidth={stroke} />
      <motion.circle
        cx={size / 2} cy={size / 2} r={radius} fill="none"
        stroke={color} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={circ}
        initial={{ strokeDashoffset: circ }}
        animate={{ strokeDashoffset: offset }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />
    </svg>
  );
};

const WeeklyGoalCard = ({ token, onGoalUpdated }) => {
  const [goalData, setGoalData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newGoal, setNewGoal] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/progress/weekly-goal`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(d => {
      setGoalData(d);
      setNewGoal(String(d.weekly_goal));
    }).catch(console.error);
  }, [token]);

  const saveGoal = async () => {
    const val = parseInt(newGoal, 10);
    if (!val || val < 1 || val > 1000) return;
    try {
      await fetch(`${BACKEND_URL}/api/progress/weekly-goal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ weekly_goal: val })
      });
      setGoalData(prev => ({
        ...prev,
        weekly_goal: val,
        progress_pct: Math.min(Math.round(prev.questions_this_week / val * 100 * 10) / 10, 100)
      }));
      setEditing(false);
      if (onGoalUpdated) onGoalUpdated();
    } catch (e) { console.error(e); }
  };

  if (!goalData) return null;

  const { weekly_goal, questions_this_week, correct_this_week, progress_pct } = goalData;
  const done = progress_pct >= 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow"
      data-testid="weekly-goal-card"
    >
      <div className="flex flex-col md:flex-row items-center gap-8">
        {/* Progress Ring */}
        <div className="relative flex-shrink-0" data-testid="weekly-goal-ring">
          <ProgressRing pct={progress_pct} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {done ? (
              <Check className="w-8 h-8 text-emerald-500" />
            ) : (
              <span className="text-2xl font-bold font-mono text-foreground">{Math.round(progress_pct)}%</span>
            )}
            <span className="text-xs text-muted-foreground mt-0.5">
              {done ? 'Complete!' : 'of goal'}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="flex-1 text-center md:text-left">
          <h2 className="text-lg font-bold mb-1">Weekly Practice Goal</h2>

          {editing ? (
            <div className="flex items-center gap-2 mt-3 justify-center md:justify-start" data-testid="goal-edit-form">
              <Input
                type="number" min={1} max={1000} value={newGoal}
                onChange={e => setNewGoal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveGoal()}
                className="w-24 font-mono text-center"
                data-testid="goal-input"
              />
              <span className="text-sm text-muted-foreground">questions / week</span>
              <Button size="sm" onClick={saveGoal} className="bg-accent hover:bg-accent/90 text-white" data-testid="goal-save-btn">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setNewGoal(String(weekly_goal)); }} data-testid="goal-cancel-btn">Cancel</Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                <span className="font-mono font-semibold text-foreground text-lg">{questions_this_week}</span>
                {' / '}
                <span className="font-mono">{weekly_goal}</span> questions this week
                {correct_this_week > 0 && (
                  <span className="ml-2 text-emerald-500">({correct_this_week} correct)</span>
                )}
              </p>
              <button
                onClick={() => setEditing(true)}
                className="mt-3 text-xs text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
                data-testid="goal-edit-btn"
              >
                <Pencil className="w-3 h-3" /> Change goal
              </button>
            </>
          )}

          {done && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2 text-sm font-medium text-emerald-500"
              data-testid="goal-complete-msg"
            >
              You hit your weekly goal — keep up the great work!
            </motion.p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const Progress = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [keyStats, setKeyStats] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${BACKEND_URL}/api/progress/stats`, { headers }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/progress/history`, { headers }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/progress/keys`, { headers }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/progress/daily`, { headers }).then(r => r.json()),
    ]).then(([statsData, historyData, keysData, dailyRes]) => {
      setStats(statsData);
      setHistory(historyData.sessions || []);
      setKeyStats(keysData.keys || []);
      setDailyData(dailyRes.daily || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  const formatTime = (seconds) => {
    if (!seconds) return '0m';
    const mins = Math.floor(seconds / 60);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
  };

  const formatMode = (mode) =>
    mode === 'number_to_chord' ? '# to Chord' : 'Chord to #';

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="progress-loading">
        <div className="text-muted-foreground text-lg">Loading progress...</div>
      </div>
    );
  }

  const empty = !stats || stats.total_sessions === 0;

  return (
    <div className="min-h-screen noise-bg" data-testid="progress-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="btn-press" data-testid="progress-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">My Progress</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/practice')} data-testid="go-practice-btn">
            <Music2 className="w-4 h-4 mr-2" />
            Practice
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12 space-y-10">
        {empty ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
            data-testid="empty-progress"
          >
            <BarChart3 className="w-16 h-16 text-muted-foreground mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-3">No practice data yet</h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Complete some practice sessions and your progress will show up here. Start practicing to track your accuracy trends!
            </p>
            <Button onClick={() => navigate('/practice')} className="bg-accent hover:bg-accent/90 text-white" data-testid="start-practice-empty-btn">
              Start Practicing
            </Button>
          </motion.div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Sessions" value={stats.total_sessions} icon={Calendar} sub={`${stats.sessions_this_week} this week`} />
              <StatCard label="Questions" value={stats.total_questions} icon={Target} sub={`${stats.total_correct} correct`} />
              <StatCard label="Accuracy" value={`${stats.overall_accuracy}%`} icon={TrendingUp}
                sub={stats.overall_accuracy >= 80 ? 'Great work!' : 'Keep practicing'}
                className={stats.overall_accuracy >= 80 ? 'ring-2 ring-emerald-500' : ''} />
              <StatCard label="Best Streak" value={stats.best_streak} icon={Flame} sub={formatTime(stats.total_practice_time) + ' total'} />
            </div>

            {/* Weekly Practice Goal */}
            <WeeklyGoalCard token={token} />

            {/* Accuracy Trend Chart */}
            {dailyData.length > 1 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow"
                data-testid="accuracy-trend-chart"
              >
                <h2 className="text-lg font-bold mb-1">Accuracy Trend</h2>
                <p className="text-sm text-muted-foreground mb-6">Your daily accuracy over time</p>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dailyData}>
                      <defs>
                        <linearGradient id="accGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 90%)" opacity={0.4} />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={formatDate} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="accuracy" name="Accuracy" stroke="hsl(24, 95%, 53%)" fill="url(#accGradient)" strokeWidth={2.5} dot={{ r: 4, fill: 'hsl(24, 95%, 53%)' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            )}

            {/* Per-Key Accuracy & Questions Per Day Side by Side */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Per-Key Accuracy */}
              {keyStats.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow"
                  data-testid="key-accuracy-chart"
                >
                  <h2 className="text-lg font-bold mb-1">Accuracy by Key</h2>
                  <p className="text-sm text-muted-foreground mb-6">Your strongest and weakest keys</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={keyStats} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 90%)" opacity={0.4} />
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} tickFormatter={v => `${v}%`} />
                        <YAxis type="category" dataKey="key" tick={{ fontSize: 13, fontFamily: 'JetBrains Mono' }} width={40} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="accuracy" name="Accuracy" fill="hsl(24, 95%, 53%)" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>
              )}

              {/* Questions Per Day */}
              {dailyData.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow"
                  data-testid="questions-per-day-chart"
                >
                  <h2 className="text-lg font-bold mb-1">Questions Per Day</h2>
                  <p className="text-sm text-muted-foreground mb-6">Your daily practice volume</p>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={dailyData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(240, 6%, 90%)" opacity={0.4} />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={formatDate} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="questions" name="Questions" fill="hsl(240, 10%, 10%)" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Recent Sessions Table */}
            {history.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-card border-2 border-border rounded-2xl p-6 brutalist-shadow"
                data-testid="recent-sessions"
              >
                <h2 className="text-lg font-bold mb-1">Recent Sessions</h2>
                <p className="text-sm text-muted-foreground mb-6">Your latest practice sessions</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="sessions-table">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium">Date</th>
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium">Mode</th>
                        <th className="text-left py-3 px-2 text-muted-foreground font-medium">Keys</th>
                        <th className="text-right py-3 px-2 text-muted-foreground font-medium">Questions</th>
                        <th className="text-right py-3 px-2 text-muted-foreground font-medium">Accuracy</th>
                        <th className="text-right py-3 px-2 text-muted-foreground font-medium">Streak</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice(0, 15).map((s, i) => (
                        <tr key={s.id || i} className="border-b border-border/50 hover:bg-secondary/30 transition-colors" data-testid={`session-row-${i}`}>
                          <td className="py-3 px-2 whitespace-nowrap">{formatDate(s.created_at)}</td>
                          <td className="py-3 px-2 font-mono text-xs">{formatMode(s.mode)}</td>
                          <td className="py-3 px-2 font-mono text-xs">{(s.keys_practiced || []).join(', ')}</td>
                          <td className="py-3 px-2 text-right font-mono">{s.questions_answered}</td>
                          <td className="py-3 px-2 text-right font-mono">
                            <span className={cn(
                              s.accuracy >= 80 ? 'text-emerald-500' :
                              s.accuracy >= 50 ? 'text-amber-500' : 'text-rose-500'
                            )}>
                              {s.accuracy}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right font-mono">{s.best_streak}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Progress;
